import { useCallback, useState } from 'react';
import type { FC, JSX } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type { Provider } from '@vgtv/api-client';

import { isHermesApp, isIOS } from '../../utils/device';
import { getQueryStringParams } from '../../utils/getQueryStringParams';
import { getAssetAccessLevel } from '../../shared/helpers/assetHelpers';
import type { ButtonProps } from '../Button/Button';

import { PaywallButton } from './PaywallButton';

const PAYWALL_TYPE_DEFAULT = 'default';
const PAYWALL_TYPE_FALLBACK = 'fallback';
// we have to link to Apple's IAP (In App Purchase) in the iOS app using deeplinks
const PAYWALL_TYPE_NATIVE_IOS = 'native';

export interface PaywallProps {
  asset: SvpAssetAdditional;
  buttonLabel: string;
  buttonSize: ButtonProps['size'];
  fallback: JSX.Element;
  access?: ProviderAccessMap[Provider];
}

export const Paywall: FC<PaywallProps> = ({
  asset,
  buttonLabel,
  buttonSize = 'large',
  fallback,
  access,
}) => {
  const [type, setType] = useState(() => {
    const params = getQueryStringParams();

    // handle forcing the type with a query string param
    // note: to get the correct url in native version you have to emulate the 'Hermes iPhone' user agent
    if (params.paywallType) {
      return params.paywallType;
    }

    return isIOS && isHermesApp
      ? PAYWALL_TYPE_NATIVE_IOS
      : PAYWALL_TYPE_DEFAULT;
  });

  const onDefaultPaywallLoadFailure = useCallback(() => {
    setType(PAYWALL_TYPE_FALLBACK);
  }, []);

  if (type === PAYWALL_TYPE_DEFAULT) {
    return (
      <PaywallButton
        asset={asset}
        buttonLabel={buttonLabel}
        buttonSize={buttonSize}
        access={access || getAssetAccessLevel(asset)!}
        onLoadFailure={onDefaultPaywallLoadFailure}
      />
    );
  }

  // if chameleon paywall fails to render we fallback to a button leading to the paywall article
  // same goes for iOS Hermes - the subscription url will be a deeplink instead
  if (type === PAYWALL_TYPE_FALLBACK || type === PAYWALL_TYPE_NATIVE_IOS) {
    return fallback;
  }

  return null;
};
